import React, { useEffect } from "react";
import { Truck, Globe, MapPin, DollarSign, Receipt, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { useCurrency } from "../context/CurrencyContext";

export default function Shipping() {
  const { settings, t } = useSettings();
  const { format } = useCurrency();
  useEffect(() => { document.title = "Shipping Info – Cheap4Teach"; }, []);
  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="shipping-page">
      <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold">{t("shipping.title", "Shipping Info")}</h1>
      <p className="c4t-desc text-base sm:text-lg mt-2">{t("shipping.subtitle", "All prices on Cheap4Teach are listed in the currency you select; including shipping & tax.")}</p>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="c4t-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="rounded-full p-3 bg-[#FFC500] border-[3px] border-black"><DollarSign className="w-5 h-5" strokeWidth={2.5} /></span>
            <h2 className="text-2xl font-['Fredoka'] font-bold">Prices</h2>
          </div>
          <p className="c4t-desc">All product prices, shipping rates, and tax are quoted in the currency chosen. Toggle between currencies (USD or JMD) in the top bar to see the converted price.</p>
        </div>
        <div className="c4t-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="rounded-full p-3 bg-[#00E0AA] border-[3px] border-black"><Globe className="w-5 h-5" strokeWidth={2.5} /></span>
            <h2 className="text-2xl font-['Fredoka'] font-bold">Worldwide Delivery</h2>
          </div>
          <p className="c4t-desc">International customers welcome. Choose "International" at checkout and we'll send a personalized quote.</p>
        </div>
        {(settings?.tax_rate ?? 0) > 0 && (
        <div className="c4t-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="rounded-full p-3 bg-[#FF1F87] border-[3px] border-black"><Receipt className="w-5 h-5 text-white" strokeWidth={2.5} /></span>
            <h2 className="text-2xl font-['Fredoka'] font-bold">{"Shipping Info"}</h2>
          </div>
          <p className="c4t-desc">A general consumption tax of {((settings?.tax_rate ?? 0) * 100).toFixed(0)}% is added to every order at checkout.</p>
        </div>
        )}
        <div className="c4t-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="rounded-full p-3 bg-[#00A6E0] border-[3px] border-black"><MapPin className="w-5 h-5 text-white" strokeWidth={2.5} /></span>
            <h2 className="text-2xl font-['Fredoka'] font-bold">Address at Checkout</h2>
          </div>
          <p className="c4t-desc">Just enter your country and shipping address at checkout. We'll handle the rest.</p>
        </div>
      </div>

      {settings?.shipping_options?.length > 0 && (
        <div className="c4t-card mt-8" data-testid="shipping-options-list">
          <div className="flex items-center gap-3 mb-4">
            <span className="rounded-full p-3 bg-[#FFC500] border-[3px] border-black"><Package className="w-5 h-5" strokeWidth={2.5} /></span>
            <h2 className="text-2xl font-['Fredoka'] font-bold">Shipping Options</h2>
          </div>
          <ul className="space-y-2">
            {settings.shipping_options.map(opt => (
              <li key={opt.name} className="flex justify-between border-b border-black/10 pb-2 last:border-none">
                <span className="font-bold">{opt.name}</span>
                <span className="text-[#FF1F87] font-bold">
                  {opt.sub_options?.length > 0 ? "Varies per location" : opt.price > 0 ? format(opt.price) : opt.name.toLowerCase().includes("international") ? "Quoted after order" : "Free"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="c4t-card mt-8 !bg-white" data-testid="shipping-note">
        <h3 className="text-xl font-['Fredoka'] font-bold mb-2 flex items-center gap-2"><Truck className="w-5 h-5"/> {t("shipping.note_title", "A Note from Cheap4Teach")}</h3>
        <p className="c4t-desc whitespace-pre-wrap">{settings?.shipping_note}</p>
      </div>

      <div className="text-center mt-10">
        <Link to="/shop" className="c4t-btn c4t-btn-pink" data-testid="shipping-shop-btn">Start Shopping</Link>
      </div>
    </div>
  );
}
